import { useState } from "react"
import { useSnackbar } from "notistack"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { sleep } from "../../../../../lib/lang"
import { Logger } from "../../../../../lib/logging"
import { toArrayAsync } from "../../../../../lib/iterables"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { AccountingDebtor, DebtorWithSalesByAccount } from "../accountingTypes"
import {
    getCustomerGroupSalesByAccounts,
    getCustomerSalesByAccounts,
    fetchCustomerGroupPayrollFilesZip,
} from "../accountingApi"
import { Box } from "@mui/system"
import { Menu, MenuItem } from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DateType } from "../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { AccountingFilterProps } from "../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"

type Props = Readonly<{
    merchantId: number
    startDate: string
    endDate: string
    debtors: AccountingDebtor[] | undefined
    dateType: DateType
    filtersList: AccountingFilterProps
}>

export function ExportButtonDebtors({ merchantId, startDate, endDate, debtors, dateType, filtersList }: Props) {
    const { customerId, userGroupId, email, creditCardLast4, orderId, employeeId } = filtersList
    const paymentMethods = filtersList.paymentMethods?.join(",")
    const merchantIds = filtersList.merchantIds?.join(",")
    const shopIds = filtersList.shopIds?.join(",")

    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const handleClickExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})

    const { mutateAsync: callGetCustomerSales } = useApiMutation(getCustomerSalesByAccounts, {})
    const { mutateAsync: callGetCustomerGroupSales } = useApiMutation(getCustomerGroupSalesByAccounts, {})
    const { mutateAsync: callFetchZip } = useApiMutation(fetchCustomerGroupPayrollFilesZip, {})

    const periodTranslation = l10n.getString("export-period")

    async function* generateReportData() {
        if (!debtors) return

        const commonFilters = {
            dateType,
            shopIds,
            paymentMethods,
            merchantIds,
            userGroupId,
            email,
            creditCardLast4,
            orderId,
            employeeId,
        }

        for (const debtor of debtors) {
            if (debtor.type === "customer") {
                const sales = await callGetCustomerSales({
                    merchantId,
                    customerId: debtor.id,
                    startDate,
                    endDate,
                    ...commonFilters,
                })

                const result: DebtorWithSalesByAccount = {
                    sales: sales.salesByAccount,
                    ...debtor,
                }

                yield result
            } else {
                const sales = await callGetCustomerGroupSales({
                    merchantId,
                    customerId: debtor.customerId,
                    customerGroupId: debtor.id,
                    startDate,
                    endDate,
                    ...commonFilters,
                })

                const result: DebtorWithSalesByAccount = {
                    sales: sales.salesByAccount,
                    ...debtor,
                }

                yield result
            }

            // Throttle the requests to avoid API-side throttling
            await sleep(250)
        }
    }

    function handleExportRows(data: DebtorWithSalesByAccount[]) {
        const csvOptions = mkConfig({
            fieldSeparator: ";",
            quoteStrings: true,
            decimalSeparator: ".",
            useBom: true,
            useKeysAsHeaders: true,
            showColumnHeaders: false,
            filename: `Facilitynet-Debtors-${startDate}_${endDate}`,
        })

        const exportRowsForBalance = data.flatMap((debtor) => [
            {
                column1: debtor.name,
                column2: "",
                column3: "",
                column4: "",
                column5: "",
                column6: "",
            },
            ...debtor.sales.categories.flatMap((category) => [
                {
                    column1: category.name,
                    column2: "",
                    column3: l10n.getString("accounting-debtors-export-items-label"),
                    column4: `${l10n.getString("accounting-debtors-export-amount-label")} (${
                        category.amount.currency
                    })`,
                    column5: l10n.getString("accounting-debtors-export-account-total"),
                    column6: l10n.getString("accounting-debtors-export-subtotal"),
                },
                {
                    column1: "",
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: "",
                },
                ...category.accounts.flatMap((account) => [
                    {
                        column1: account.name,
                        column2: "",
                        column3: "",
                        column4: "",
                        column5: moneyFactory.formatAsNumber(toDinero(account.amount)),
                        column6: "",
                    },
                    ...(account.products ?? []).map((product) => ({
                        column1: product.name,
                        column2: product.code,
                        column3: product.numberOfItems,
                        column4: moneyFactory.formatAsNumber(toDinero(product.totalAmount)),
                        column5: "",
                        column6: "",
                    })),
                ]),
                {
                    column1: "",
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: "",
                },
                {
                    column1: `${l10n.getString("accounting-debtors-export-subtotal-label")} (${category.name})`,
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: moneyFactory.formatAsNumber(toDinero(category.amount)),
                },
                {
                    column1: "",
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: "",
                },
            ]),
            {
                column1: "",
                column2: "",
                column3: "",
                column4: "",
                column5: "",
                column6: "",
            },
        ])

        exportRowsForBalance.unshift({
            column1: "",
            column2: "",
            column3: "",
            column4: "",
            column5: "",
            column6: "",
        })

        exportRowsForBalance.unshift({
            column1: `${periodTranslation}: ${startDate} - ${endDate}`,
            column2: "",
            column3: "",
            column4: "",
            column5: "",
            column6: "",
        })

        const csv = generateCsv(csvOptions)(exportRowsForBalance)
        download(csvOptions)(csv)
    }

    function handleClickDefaultExport() {
        handleClose()
        const logger = new Logger("debitors")
        setLoading(true)

        toArrayAsync(generateReportData())
            .then((data) => handleExportRows(data))
            .catch((error) => {
                logger.error("Failed to export debitors", error)

                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("accounting-debtors-export-failed"),
                })
            })
            .finally(() => setLoading(false))
    }

    async function handleZipExport() {
        handleClose()
        setLoading(true)
        try {
            const blob = await callFetchZip({ merchantId, startDate, endDate, dateType, shopIds, paymentMethods, merchantIds, customerId, userGroupId, email, creditCardLast4, orderId, employeeId })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = `Facilitynet-PayrollFiles-${startDate}_${endDate}.zip`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            enqueueSnackbar({
                variant: "error",
                message: l10n.getString("accounting-debtors-export-failed"),
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box>
            <StandardExportButton loading={loading} onClick={handleClickExportClick} endIcon={<ArrowDropDownIcon />} />
            <Menu
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <LocalizedStrict id="accounting-customers-export-sales-summary">
                    <MenuItem onClick={handleClickDefaultExport}>Customer sales summary</MenuItem>
                </LocalizedStrict>
                <LocalizedStrict id="accounting-customers-export-payroll">
                    <MenuItem onClick={handleZipExport}>Customer payroll files (ZIP file)</MenuItem>
                </LocalizedStrict>
            </Menu>
        </Box>
    )

    // return <StandardExportButton loading={loading} onClick={handleClick} />
}
